import React from 'react';
import { Building2, Pencil, Plus, UserCheck, UserX } from 'lucide-react';

import {
  CardAction,
  ChipRow,
  EmptyState,
  PageHeader,
  PhonePage,
  PillButton,
  ScreenLoader,
  SearchBar,
  StatusPill,
} from '@/components/phone/primitives';
import { Colors } from '@/components/phone/theme';

/**
 * The outside vendors, on a phone.
 *
 * A card per vendor says who they are, how to reach them, which stages they
 * take work at and how much of ours they are holding — the one number the
 * desktop list was rebuilt around. The status tabs are chips.
 */

function VendorCard({ vendor: v, onEdit, onToggle }) {
  const active = v.status === 'active';
  const contact = [v.mobile_number, v.email].filter(Boolean).join(' · ');
  const stages = v.linked_stages || [];

  return (
    <li
      data-testid={`vendor-card-${v.id}`}
      className="mt-3 rounded-[10px] border"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <div className="px-4 pt-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>{v.full_name}</p>
            <p className="truncate text-[12px]" style={{ color: Colors.text.tertiary }}>{v.username}</p>
          </div>
          <StatusPill label={v.status} tone={active ? 'positive' : 'neutral'} dot />
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {stages.length === 0 && (
            <span className="text-[12px]" style={{ color: Colors.text.tertiary }}>No stages linked</span>
          )}
          {stages.slice(0, 3).map((s) => <StatusPill key={s} label={s} tone="caution" />)}
          {stages.length > 3 && (
            <span className="text-[11px]" style={{ color: Colors.text.tertiary }}>+{stages.length - 3}</span>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 py-3 text-[12px]">
          <span className="truncate" style={{ color: Colors.text.secondary }}>{contact || 'No contact on file'}</span>
          <span
            className="shrink-0 font-semibold tabular-nums"
            style={{ color: v.active_orders_count ? Colors.accent.DEFAULT : Colors.text.tertiary }}
            data-testid={`vendor-active-${v.id}`}
          >
            {v.active_orders_count || 0} active
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 border-t px-3 py-2" style={{ borderColor: Colors.border.subtle }}>
        <CardAction icon={Pencil} label="Edit vendor" onClick={onEdit} testID={`edit-vendor-${v.id}`} />
        <CardAction
          icon={active ? UserX : UserCheck}
          label={active ? 'Deactivate vendor' : 'Activate vendor'}
          tone={active ? 'critical' : 'positive'}
          onClick={onToggle}
          testID={`toggle-vendor-${v.id}`}
        />
      </div>
    </li>
  );
}

export default function PhoneVendorsList({
  vendors, total, activeCount, loading, onSearch, statusFilter, onStatusFilter, onNew, onEdit, onToggle,
}) {
  const chips = [
    { key: null, label: `All ${total}` },
    { key: 'active', label: `Active ${activeCount}` },
    { key: 'inactive', label: `Inactive ${total - activeCount}` },
  ];

  return (
    <PhonePage testID="vendor-management-page">
      <PageHeader
        title="Vendors"
        subtitle="External vendors, binders and city office partners"
        action={(
          <PillButton onClick={onNew} testID="add-vendor-btn">
            <Plus className="h-4 w-4" /> New
          </PillButton>
        )}
      />

      <div className="px-5">
        <SearchBar placeholder="Name, username, stage…" onChange={onSearch} testID="vendors-search-input" />
        <div className="mt-3">
          <ChipRow chips={chips} active={statusFilter} onSelect={onStatusFilter} testIDPrefix="chip" />
        </div>
      </div>

      <div className="flex-1 px-5 pb-8">
        {loading ? (
          <ScreenLoader />
        ) : vendors.length === 0 ? (
          <EmptyState icon={Building2} message="No vendors yet" hint="Add your first one." />
        ) : (
          <ul data-testid="vendors-list">
            {vendors.map((v) => (
              <VendorCard key={v.id} vendor={v} onEdit={() => onEdit(v)} onToggle={() => onToggle(v)} />
            ))}
          </ul>
        )}
      </div>
    </PhonePage>
  );
}
